// Copyright ©️ 2026 eVolve MEP, LLC

import { type ReactNode, useMemo } from 'react';

import type { WorkOrderPaginated, EvolveApiParams, Facility, Project } from '@evolvemep/api-ts';

import type { SearchableSelectProps } from 'components/Mantine/TypeSafeSelect/PaginatedSelectors/SearchableSelect';
import { hasLengthExactly } from 'helpers/helper-types';
import { useWorkOrderStatuses } from 'hooks/typeCacheHooks/useWorkOrderStatuses';
import { WorkOrderSearchableSelect } from 'modules/Shop/EvolveFabrication/WorkOrderSearchableSelect';
import { useGetWorkOrderStatesFromTruthTable } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/useCanEditWorkOrder';

type BaseProps = {
  value?: WorkOrderPaginated | null;
  onChange: (workOrder: WorkOrderPaginated | null) => void;
  selectedFacilities?: Facility[];
  selectedProjects: Project[];
  error?: ReactNode;
};

type Props = BaseProps &
  Omit<
    SearchableSelectProps<'shop/workOrder', WorkOrderPaginated, 'workOrderId'>,
    'idKey' | 'paginatedGet' | 'getItemLabel' | keyof BaseProps
  >;

/**
 * A Searchable Select for choosing a Work Order to add Fabrication elements to.
 */
export const WorkOrderSearchableSelectForFabrication = ({ selectedFacilities, selectedProjects, ...props }: Props) => {
  const { states } = useWorkOrderStatuses();
  const { getWorkOrderStatesFromTruthTable } = useGetWorkOrderStatesFromTruthTable();

  const validStates = useMemo(() => {
    const editableStates = getWorkOrderStatesFromTruthTable('editItems');
    return states?.filter((s) => editableStates.includes(s.name)).map((s) => s.stateId);
  }, [getWorkOrderStatesFromTruthTable, states]);

  const filters = useMemo<EvolveApiParams<'GET_PAGINATED', 'shop/workOrder'>>(
    () => ({
      facilityIds: selectedFacilities?.map((f) => f.facilityId),
      projectIds: selectedProjects.map((p) => p.projectId),
      workOrderStateIds: validStates,
    }),
    [selectedFacilities, selectedProjects, validStates],
  );

  return (
    <WorkOrderSearchableSelect
      filters={filters}
      preloadData
      required
      defaultProject={hasLengthExactly(selectedProjects, 1) ? selectedProjects[0] : undefined}
      disableProjectSelection
      {...props}
    />
  );
};
